<template>
  <div>
    <Head :title="__(title)"/>
    <div class="flex flex-col md:flex-row gap-3 mb-4 justify-between items-center ticket-filters">
      <search-input v-model="form.search" placeholder="Search by Key, Subject, Priority, Status, Assign to..."
                    class="w-full max-w-md search" @reset="reset"></search-input>
      <div class="filter-add-new flex flex-col gap-3 md:flex-row items-center">
        <a class="uppercase gap-[1px] text-sm px-3 py-2 flex items-center justify-center"
           href="/dashboard/ticket/csv/export">
          <img class="w-6 h-6" src="/images/svg/export-csv.svg" alt="Export CSV"/>
          <span>{{ __('Export') }}</span>
        </a>
        <label for="importCSV" class="uppercase gap-[1px] text-sm px-3 py-2 flex items-center justify-center">
          <img class="w-6 h-6" src="/images/svg/import-csv.svg" alt="Import CSV"/>
          <span>{{ __('Import') }}</span>
          <input @change="uploadImportCSV" class="hidden" id="importCSV" type="file"/>
        </label>
        <select v-model="form.limit"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        <Link class="btn-indigo" :href="this.route('tickets.create')">
          <span>{{ __('New Ticket') }}</span>
        </Link>
      </div>
    </div>
    <div class="flex flex-col gap-3 mb-4 md:flex-row w-full items-center ticket-filters">
      <div class="mr-2 w-full">{{ __('Filter Ticket By') }}:</div>
      <select-input v-if="!(hidden_fields && hidden_fields.includes('ticket_type'))" v-model="form.type_id"
                    class="mr-2 w-full">
        <option :value="null">{{ __('Type') }}</option>
        <option v-for="s in types" :key="s.id" :value="s.id">{{ s.name }}</option>
      </select-input>
      <select-input v-if="!(hidden_fields && hidden_fields.includes('category'))" v-model="form.category_id"
                    class="mr-2 w-full">
        <option :value="null">{{ __('Category') }}</option>
        <option v-for="s in categories" :key="s.id" :value="s.id">{{ s.name }}</option>
      </select-input>
      <select-input v-if="!(hidden_fields && hidden_fields.includes('department'))" v-model="form.department_id"
                    class="mr-2 w-full">
        <option :value="null">{{ __('Department') }}</option>
        <option v-for="s in departments" :key="s.id" :value="s.id">{{ s.name }}</option>
      </select-input>
      <select-input v-model="form.priority_id" class=" mr-2 w-full">
        <option :value="null">{{ __('Priority') }}</option>
        <option v-for="s in priorities" :key="s.id" :value="s.id">{{ s.name }} {{
            s.sla_time ? '(' + s.sla_time + ' ' + __('Hours') + ')' : ''
          }}
        </option>
      </select-input>
      <select-input v-model="form.status_id" class="mr-2 w-full">
        <option :value="null">{{ __('Status') }}</option>
        <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
      </select-input>
      <select-input-filter :placeholder="__('Assign To')" :onInput="doFilter" @focus="doFilter" :items="assignees"
                           v-if="!(hidden_fields && hidden_fields.includes('assigned_to')) && user_access.ticket.update"
                           v-model="form.assigned_to" class=" w-full">
      </select-input-filter>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="min-w-full whitespace-nowrap ticket_list">
        <tr class="text-left font-bold">
          <th v-for="(h, i) in headers" :key="i">
            <span :class="{'sort': h.sort, 'active' : form.field === h.name},form.direction">{{ __(h.name) }}
              <span v-if="h.sort" class="icons">
                <icon class="fill-gray-300"
                      :class="{'fill-gray-800': (form.direction === 'desc' && form.field === h.value)}" name="up"
                      @click="sort(h.value)"/>
                <icon class="fill-gray-300"
                      :class="{'fill-gray-800': form.direction === 'asc' && form.field === h.value}" name="down"
                      @click="sort(h.value)"/>
              </span>
            </span>
          </th>
        </tr>
        <tr v-for="ticket in tickets_" :key="ticket.id"
            class="hover:cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
            @click="goToTicketDetail(ticket.uid)">
          <td class="border-t">
            <!--            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <!--              #{{ ticket.uid }}-->
            <!--            </Link>-->
            <div class="flex items-center px-6 py-4 focus:text-indigo-500"
                 :href="route('tickets.edit', ticket.uid || ticket.id)">
              #{{ ticket.id }}
            </div>
          </td>
          <td class="border-t">
            <div class="font-light text-sm flex gap-2">
              <div v-for="(tag, index) in ticket.tags" :key="index"
                   :class="['bg-gray-200', 'rounded-full', 'px-2', 'py-1', { 'mr-8': index === ticket.tags.length - 1 }]">
                {{ tag.name }}
              </div>
            </div>
          </td>
          <td class="border-t">
            <!--            <Link class="s__details flex flex-col" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <div class="s__details flex flex-col">
              <span class="subject_t">{{ ticket.subject }}</span>
              <span class="user__d flex text-xs items-center pt-1">
                <span v-if="ticket.user" class="user__n flex items-center pr-4" title="Client">
                  <icon name="user" class="flex-shrink-0 h-3 fill-gray-400 pr-1"/>
                  {{ ticket.user }}
                </span>
                <span v-if="ticket.assigned_to" class="user__n flex items-center pr-4" title="Assignee">
                  <icon name="user-check" class="flex-shrink-0 h-3 fill-gray-400 pr-1"/>
                  {{ ticket.assigned_to }}
                </span>
              </span>
            </div>
            <!--            </Link>-->

          </td>
          <td class="border-t">
            <!--            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <div class="flex items-center px-6 py-4 focus:text-indigo-500"
                 :href="route('tickets.edit', ticket.uid || ticket.id)">
              {{ ticket.priority }}
            </div>
          </td>
          <td class="border-t">
            <!--            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <div class="flex items-center px-6 py-4 focus:text-indigo-500">
              {{ ticket.status }}
            </div>
          </td>
          <td class="border-t">
            <!--            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <div class="flex items-center px-6 py-4 focus:text-indigo-500">
              {{
                __('error') === 'error' ? moment(ticket.created_at).fromNow() : moment(ticket.created_at).locale('zh-tw').fromNow()
              }}
            </div>
          </td>
          <td class="border-t">
            <!--            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <div class="flex items-center px-6 py-4 focus:text-indigo-500">
              {{
                __('error') === 'error' ? moment(ticket.updated_at).fromNow() : moment(ticket.updated_at).locale('zh-tw').fromNow()
              }}
            </div>
          </td>
          <td class="border-t">
            <!--            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">-->
            <div class="flex items-center px-6 py-4 focus:text-indigo-500">
              <span :style="getSlaTimeLeftStyle(ticket)">{{ getSlaTimeLeft(ticket) }}</span>
            </div>
          </td>
        </tr>
        <tr v-if="tickets_.length === 0">
          <td class="border-t px-6 py-4" colspan="9">{{ __('No ticket found.') }}</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-4" :links="tickets_link"/>
  </div>
</template>

<script>
import {Head, Link} from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SelectInput from '@/Shared/SelectInput'
import SearchInput from '@/Shared/SearchInput'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import moment from 'moment'
import axios from 'axios'

export default {
  metaInfo: {title: 'Tickets'},
  components: {
    SearchInput,
    Icon,
    Link,
    Head,
    Pagination,
    SelectInputFilter,
    SelectInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    tickets: Object,
    assignees: Array,
    auth: Object,
    title: String,
    priorities: Array,
    statuses: Array,
    types: Array,
    categories: Array,
    departments: Array,
    hidden_fields: Object,
  },
  remember: 'form',
  data() {
    return {
      headers: [
        {name: 'Key', value: 'id', sort: true},
        {name: 'Tag', value: 'tag', sort: false},
        {name: 'Subject', value: 'subject', sort: true},
        {name: 'Priority', value: 'priority_id', sort: true},
        {name: 'Status', value: 'status_id', sort: true},
        {name: 'Created', value: 'created_at', sort: true},
        {name: 'Updated', value: 'updated_at', sort: true},
        {name: 'SLA', value: 'sla_time', sort: false},
      ],
      user_access: this.$page.props.auth.user.access,
      form: {
        search: this.filters.search,
        limit: this.filters.limit ?? 10,
        customer_id: this.filters.customer_id,
        field: this.filters.field,
        direction: this.filters.direction,
        priority_id: this.filters.priority_id ?? null,
        status_id: this.filters.status_id ?? null,
        type_id: this.filters.type_id ?? null,
        category_id: this.filters.category_id ?? null,
        department_id: this.filters.department_id ?? null,
      },
      tickets_: this.tickets.data || [],
      tickets_link: this.tickets.links,
    }
  },
  methods: {
    doFilter(e) {
      axios.get(this.route('filter.assignees', {search: e.target.value})).then((res) => {
        this.assignees.splice(0, this.assignees.length, ...res.data);
      })
    },
    sort(field) {
      this.form.field = field;
      this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc';
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    uploadImportCSV(e) {
      if (e.target.files.length) {
        this.$inertia.form({file: e.target.files[0]}).post(this.route('ticket.csv.import'))
      }
    },
    getSlaTimeLeft(ticket) {
      if (ticket.sla_breach && ticket.sla_breach_reason === 'first_response_sla') {
        return 'Fail: First Response';
      }

      if (ticket.sla_breach && ticket.sla_breach_reason === 'priority_sla') {
        return 'Fail: Priority';
      }

      // Otherwise, we calculate the time left
      const updatedAt = moment(ticket.updated_at);
      const slaDeadline = moment(ticket.created_at).add({hours: 48});
      const now = moment();

      // if updated_at is in range between created_at and created_at + 48 hours, then the ticket is already responded
      // so, we don't need to calculate the time left from created_at -- we then say SLA passed
      // updated_at must not be the same as created_at
      if (updatedAt !== moment(ticket.created_at) && updatedAt.isBetween(moment(ticket.created_at), slaDeadline)) {
        return 'Passed';
      }

      // else, we calculate the time left
      const duration = moment.duration(slaDeadline.diff(now));
      if (duration.asMilliseconds() < 0) {
        return 'Fail: First Response';
      }

      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.minutes());

      return `${hours} hours and ${minutes} minutes left`;
    },
    getSlaTimeLeftStyle(ticket) {
      if (ticket.sla_breach && ticket.sla_breach_reason === 'first_response_sla') {
        return 'color: red; font-weight: bold';
      }

      if (ticket.sla_breach && ticket.sla_breach_reason === 'priority_sla') {
        return 'color: red; font-weight: bold';
      }

      // Otherwise, we calculate the time left
      const updatedAt = moment(ticket.updated_at);
      const slaDeadline = moment(ticket.created_at).add({hours: 48});
      const now = moment();

      // if updated_at is in range between created_at and created_at + 48 hours, then the ticket is already responded
      // so, we don't need to calculate the time left from created_at -- we then say SLA passed
      // updated_at must not be the same as created_at
      if (updatedAt !== moment(ticket.created_at) && updatedAt.isBetween(moment(ticket.created_at), slaDeadline)) {
        return 'color: green; font-weight: bold';
      }

      // else, we calculate the time left
      const duration = moment.duration(slaDeadline.diff(now));
      if (duration.asMilliseconds() < 0) {
        return 'color: red; font-weight: bold';
      }

      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.minutes());

      return 'color: black; font-weight: bold';
    },
    updateSlaTimes() {
      this.tickets_ = this.tickets_.map(ticket => ({
        ...ticket,
        slaTimeLeft: this.getSlaTimeLeft(ticket),
        slaTimeLeftStyle: this.getSlaTimeLeftStyle(ticket),
      }));
    },
    convertToSerializableObject(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
    goToTicketDetail(ticket) {
      this.$inertia.visit(this.route('tickets.edit', ticket));
    }
  },
  created() {
    this.moment = moment;
    this.updateSlaTimes();
  },
  watch: {
    form: {
      deep: true,
      handler: function () {
        this.$inertia.get(
          this.route('tickets'),
          pickBy(this.form),
          //{replace: true, preserveState: true}
        )
      }
    },
  },
  mounted() {
    // get new tickets every 5 seconds
    setInterval(() => {
      // if url is not tickets page, then don't get new tickets
      if (window.location.pathname !== '/dashboard/tickets') {
        return;
      }

      // get page from url
      const urlParams = new URLSearchParams(window.location.search);
      const page = urlParams.get('page');

      // if page is null, then it's the first page
      if (page === null) {
        this.$inertia.get(
          this.route('tickets'),
          pickBy(this.form),
          //{replace: true, preserveState: true}
        )
      }
    }, 60000);
    setInterval(() => {
      this.updateSlaTimes();
    }, 5000);
  }
}
</script>
